"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import ChevronLeftIcon from "../../components/Icons/ChevronLeftIcon";
import BookOpenIcon from "../../components/Icons/BookOpenIcon";
import ChevronDownIcon from "../../components/Icons/ChevronDownIcon";
import ChevronRightIcon from "../../components/Icons/ChevronRightIcon";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  BarChart3,
  BookOpen,
  Gauge,
  Landmark,
  Network,
  CircleCheck,
  Share2,
  Users,
  ShoppingBag,
  LayoutGrid,
  FileText,
  Megaphone,
  UsersRound,
  MapPin,
  ListChecks,
  ShoppingCart,
  FileBarChart,
  Upload,
} from "lucide-react";

const BASE_PATH = "/documentation";

// Pure CSS state — no inline styles, no imperative mouseenter/mouseleave DOM
// mutation. That old approach could leave a stale "active" background applied
// to a link the mouse had already left (a fast pointer move between two
// adjacent items could fire the new item's enter before the old one's leave),
// which is exactly the "two items stuck highlighted" bug this replaces.
// Classic left-accent nav pattern (VS Code / GitHub docs style) rather than a
// rounded SaaS pill: a solid border on the current page, a quiet tint on
// hover, both clearly different from each other and from the solid-fill
// "active" pill this replaces.
const NAV_LINK_BASE =
  "transition-colors duration-150 border-l-[3px] rounded-r-[6px]";
const getNavLinkClasses = (isActive) =>
  isActive
    ? `${NAV_LINK_BASE} border-[#171717] bg-[#F4F4F5] text-[#171717] font-semibold`
    : `${NAV_LINK_BASE} border-transparent text-[#525252] hover:bg-[#F7F7F7] hover:text-[#171717] hover:border-[#D4D4D4]`;

// Submodule styling preserves the existing tree navigation.
const getSubNavLinkClasses = (isActive) =>
  isActive
    ? "transition-colors duration-150 rounded-md bg-[#F4F4F5] text-[#171717] font-medium"
    : "transition-colors duration-150 rounded-md text-[#525252] hover:bg-[#F7F7F7] hover:text-[#171717]";

const docsStructure = {
  crm: {
    name: "CRM",
    Icon: BarChart3,
    iconColor: "#171717",
    iconBg: "#F5F5F5",
    // Flat list mirroring the ABSS CRM app sidebar (headers + direct links).
    flat: true,
    items: [
      {
        kind: "link",
        label: "About ABSS CRM",
        path: "getting-started/about",
        Icon: BookOpen,
      },
      { kind: "link", label: "Dashboard", path: "home/dashboard", Icon: Gauge },
      { kind: "header", label: "All Master" },
      { kind: "link", label: "Bank", path: "banking/bank", Icon: Landmark },
      { kind: "subheader", label: "Lead Management" },
      {
        kind: "link",
        label: "Lead Industry",
        path: "all-master/lead-industry",
        Icon: Network,
        indent: true,
      },
      {
        kind: "link",
        label: "Lead Status",
        path: "all-master/lead-status",
        Icon: CircleCheck,
        indent: true,
      },
      {
        kind: "link",
        label: "Lead Source",
        path: "all-master/lead-source",
        Icon: Share2,
        indent: true,
      },
      {
        kind: "link",
        label: "Customer",
        path: "all-master/customer",
        Icon: Users,
      },
      {
        kind: "link",
        label: "Item",
        path: "all-master/item",
        Icon: ShoppingBag,
      },
      {
        kind: "link",
        label: "Item Group",
        path: "all-master/item-group",
        Icon: LayoutGrid,
      },
      {
        kind: "link",
        label: "Sales Order Tag",
        path: "all-master/sales-order-tag",
        Icon: FileText,
      },
      {
        kind: "link",
        label: "Campaigns",
        path: "sales/campaigns",
        Icon: Megaphone,
      },
      { kind: "link", label: "Leads", path: "sales/leads", Icon: UsersRound },
      {
        kind: "link",
        label: "Bulk Lead Import",
        path: "sales/bulk-lead-import",
        Icon: Upload,
      },
      { kind: "link", label: "Visit", path: "sales/visit", Icon: MapPin },
      { kind: "link", label: "Task", path: "sales/task", Icon: ListChecks },
      {
        kind: "link",
        label: "Quotations",
        path: "sales/quotations",
        Icon: FileText,
      },
      {
        kind: "link",
        label: "Sales Orders",
        path: "sales/sales-orders",
        Icon: ShoppingCart,
      },
      {
        kind: "link",
        label: "Reports",
        path: "reports/reports",
        Icon: FileBarChart,
      },
    ],
  },
  books: {
    name: "Books",
    Icon: BookOpen,
    iconColor: "#171717",
    iconBg: "#F5F5F5",
    modules: [
      {
        id: "getting-started",
        name: "Getting Started",
        submodules: [
          { id: "welcome", name: "Welcome" },
          { id: "setting-up-your-account", name: "Setting Up Your Account" },
          { id: "finding-your-way-around", name: "Finding Your Way Around" },
          { id: "about", name: "About ABSS Books" },
        ],
      },
      {
        id: "bulk-import",
        name: "Bulk Import",
        submodules: [
          { id: "customers", name: "Customers & Vendors" },
          { id: "items", name: "Items" },
          { id: "opening-stock", name: "Opening Stock" },
          { id: "opening-balance", name: "Opening Balances" },
        ],
      },
      {
        id: "home",
        name: "Home",
        submodules: [{ id: "dashboard", name: "Dashboard" }],
      },
      {
        id: "banking",
        name: "Banking",
        submodules: [
          { id: "banks", name: "Banks" },
          { id: "bank-transfer-logs", name: "Bank Transfer Logs" },
        ],
      },
      {
        id: "all-master",
        name: "All Master",
        submodules: [
          { id: "item-master", name: "Item" },
          { id: "quality-master", name: "Quality" },
          { id: "division", name: "Division" },
          { id: "branch", name: "Branch" },
          { id: "warehouse-master", name: "Warehouse" },
          { id: "process", name: "Process" },
          { id: "recipe", name: "Recipe" },
          { id: "price-list", name: "Price List" },
          { id: "item-group", name: "Item Group" },
          { id: "manpower", name: "ManPower" },
          { id: "sales-order-tag", name: "Sales Order Tag" },
        ],
      },
      {
        id: "production",
        name: "Production",
        submodules: [
          { id: "add-production", name: "Add Production" },
          { id: "submit-production", name: "Submit Production" },
        ],
      },
      {
        id: "sales",
        name: "Sales",
        submodules: [
          { id: "customer-master", name: "Customer" },
          { id: "quotes", name: "Quotes" },
          { id: "customer-orders", name: "Customer Orders" },
          { id: "sales-orders", name: "Sales Orders" },
          { id: "delivery-challans", name: "Delivery Challans" },
          { id: "invoices", name: "Invoices" },
          { id: "payments-received", name: "Payments Received" },
          { id: "recurring-invoices", name: "Recurring Invoices" },
          { id: "credit-notes", name: "Credit Notes" },
        ],
      },
      {
        id: "purchase",
        name: "Purchase",
        submodules: [
          { id: "vendors", name: "Vendors" },
          { id: "expenses", name: "Expenses" },
          { id: "recurring-expenses", name: "Recurring Expenses" },
          { id: "purchase-orders", name: "Purchase Orders" },
          { id: "grpo", name: "GRPO" },
          { id: "bills", name: "Bills" },
          { id: "payments-made", name: "Payments Made" },
          { id: "recurring-bills", name: "Recurring Bills" },
          { id: "debit-notes", name: "Debit Notes" },
        ],
      },
      {
        id: "accountant",
        name: "Accountant",
        submodules: [
          { id: "chart-of-accounts", name: "Chart of Accounts" },
          { id: "account-ledger", name: "Account Ledger" },
          { id: "manual-journals", name: "Manual Journals" },
        ],
      },
      {
        id: "inventory",
        name: "Inventory",
        submodules: [
          { id: "view-inventory", name: "View Inventory" },
          { id: "inventory-transfer", name: "Inventory Transfer" },
          { id: "inventory-adjustment", name: "Inventory Adjustment" },
        ],
      },
      {
        id: "reports",
        name: "Reports",
        submodules: [{ id: "reports-overview", name: "Reports Overview" }],
      },
      {
        id: "analytics",
        name: "Analytics",
        submodules: [{ id: "analytics-overview", name: "Analytics" }],
      },
      {
        id: "settings",
        name: "Settings",
        submodules: [
          { id: "my-profile", name: "My Profile" },
          { id: "profile", name: "Profile" },
          { id: "currencies", name: "Currencies" },
          { id: "whatsapp-config", name: "WhatsApp Config" },
          { id: "gst-portal-credentials", name: "GST Portal Credentials" },
          { id: "manage-subscription", name: "Manage Subscription" },
          { id: "users", name: "Users" },
          { id: "roles", name: "Roles" },
          { id: "quotation-config", name: "Quotation Config" },
          { id: "inventory-config", name: "Inventory Config" },
          { id: "additional-charges", name: "Additional Charges" },
          { id: "discounts", name: "Discounts" },
          { id: "credit-limits", name: "Credit Limits" },
          { id: "organization-defaults", name: "Organization Defaults" },
          { id: "notifications", name: "Notifications" },
        ],
      },
      {
        id: "support",
        name: "Customer Support",
        submodules: [{ id: "support", name: "Support" }],
      },
    ],
  },
};

export default function DocsSidebar({ isMobileMenuOpen = false, onClose }) {
  const pathname = usePathname();
  const [expandedModules, setExpandedModules] = useState([]);
  const isInitialized = useRef(false);

  const getCurrentProduct = () => {
    if (!pathname) return null;
    const match = pathname.match(new RegExp(`^${BASE_PATH}/([^/]+)`));
    return match ? match[1] : null;
  };

  const currentProduct = getCurrentProduct();
  const currentProductData = currentProduct
    ? docsStructure[currentProduct]
    : null;

  const isActiveLink = useCallback(
    (productId, moduleId, submoduleId) => {
      if (!pathname) return false;
      if (submoduleId) {
        return (
          pathname === `${BASE_PATH}/${productId}/${moduleId}/${submoduleId}`
        );
      }
      return pathname === `${BASE_PATH}/${productId}/${moduleId}`;
    },
    [pathname],
  );

  const toggleModule = useCallback((moduleId) => {
    setExpandedModules((prev) => {
      const newState = prev.includes(moduleId) ? [] : [moduleId];
      if (typeof window !== "undefined") {
        localStorage.setItem(
          "documentation-expanded-modules",
          JSON.stringify(newState),
        );
      }
      return newState;
    });
  }, []);

  const handleSubmoduleClick = useCallback((moduleId) => {
    setExpandedModules(() => {
      const newState = [moduleId];
      if (typeof window !== "undefined") {
        localStorage.setItem(
          "documentation-expanded-modules",
          JSON.stringify(newState),
        );
      }
      return newState;
    });
  }, []);

  useEffect(() => {
    if (!isInitialized.current && typeof window !== "undefined") {
      setExpandedModules([]);
      isInitialized.current = true;
    }
  }, []);

  useEffect(() => {
    if (currentProductData && pathname && isInitialized.current) {
      const pathParts = pathname.split("/");
      if (pathParts.length === 5) {
        const moduleId = pathParts[3];
        setExpandedModules((prev) => {
          if (prev.length !== 1 || !prev.includes(moduleId)) {
            const newState = [moduleId];
            if (typeof window !== "undefined") {
              localStorage.setItem(
                "documentation-expanded-modules",
                JSON.stringify(newState),
              );
            }
            return newState;
          }
          return prev;
        });
      }
    }
  }, [currentProductData, pathname]);

  useEffect(() => {
    if (isInitialized.current && typeof window !== "undefined") {
      localStorage.setItem(
        "documentation-expanded-modules",
        JSON.stringify(expandedModules),
      );
    }
  }, [expandedModules]);

  const modulesData = useMemo(() => {
    if (!currentProductData || !currentProductData.modules) return [];
    return currentProductData.modules.map((module) => ({
      ...module,
      hasSubmodules: module.submodules && module.submodules.length > 0,
      isExpanded: expandedModules.includes(module.id),
      isModuleActive: isActiveLink(currentProduct, module.id),
    }));
  }, [currentProductData, currentProduct, expandedModules, isActiveLink]);

  const SidebarContent = useMemo(
    () => (
      <div className="docs-sidebar-content h-full flex flex-col bg-white border-r border-[#E5E5E5]">
        {/* Logo - aligns with DocsHeader height */}
        <div className="flex items-center h-14 sm:h-[65px] px-4 sm:px-6 flex-shrink-0 border-b border-[#E5E5E5]">
          <Link href="/" className="flex items-center flex-shrink-0">
            <div className="flex items-center">
              <img src="/ABSS-LOGO.png" alt="ABSS" width={190} height={70} />
            </div>
          </Link>
        </div>

        <div className="pt-2 px-6">
          {currentProductData ? (
            <div>
              <Link
                href={BASE_PATH}
                className="flex items-center space-x-2 text-sm text-[#525252] hover:text-[#171717] transition-colors mb-4 geist-regular"
                onClick={onClose}
              >
                <ChevronLeftIcon className="w-4 h-4" />
                <span>All Products</span>
              </Link>
              <div className="flex items-center space-x-3">
                <div
                  className="w-9 h-9 rounded-[8px] flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: currentProductData.iconBg }}
                >
                  <currentProductData.Icon
                    className="w-5 h-5"
                    style={{ color: currentProductData.iconColor }}
                  />
                </div>
                <div>
                  <h2 className="text-xl font-medium text-[#171717] sentient-medium">
                    {currentProductData.name}
                  </h2>
                  <p className="text-xs text-[#525252] geist-regular">
                    Documentation
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <Link
              href={BASE_PATH}
              className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
              onClick={onClose}
            >
              <BookOpenIcon className="w-6 h-6 text-[#171717]" />
              <span className="text-xl font-medium text-[#171717] sentient-medium">
                All Products
              </span>
            </Link>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto p-4">
          {currentProductData && currentProductData.flat ? (
            <div className="space-y-0.5">
              {currentProductData.items.map((item, i) => {
                if (item.kind === "header") {
                  return (
                    <p
                      key={`h-${i}`}
                      className="px-4 pt-4 pb-1.5 text-[11px] font-semibold uppercase tracking-widest text-[#A3A3A3] geist-semibold"
                    >
                      {item.label}
                    </p>
                  );
                }
                if (item.kind === "subheader") {
                  return (
                    <p
                      key={`sh-${i}`}
                      className="px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-[#94a3b8] geist-semibold"
                    >
                      {item.label}
                    </p>
                  );
                }
                const href = `${BASE_PATH}/${currentProduct}/${item.path}`;
                const isActive = pathname === href;
                const ItemIcon = item.Icon;
                return (
                  <Link
                    key={item.path}
                    href={href}
                    className={`flex items-center gap-2.5 py-2 text-sm ${
                      item.indent ? "pl-8 pr-4" : "px-4"
                    } ${getNavLinkClasses(isActive)}`}
                    onClick={onClose}
                  >
                    {ItemIcon && <ItemIcon className="w-4 h-4 flex-shrink-0" />}
                    <span className="geist-regular">{item.label}</span>
                  </Link>
                );
              })}
            </div>
          ) : currentProductData ? (
            <div>
              {modulesData.map((module) => (
                <div key={module.id}>
                  <div className="flex items-center">
                    {module.hasSubmodules ? (
                      <button
                        onClick={() => toggleModule(module.id)}
                        className={`flex items-center justify-between w-full px-4 py-2 text-sm ${getNavLinkClasses(module.isModuleActive)}`}
                      >
                        <span className="geist-regular">{module.name}</span>
                        {module.isExpanded ? (
                          <ChevronDownIcon className="w-4 h-4" />
                        ) : (
                          <ChevronRightIcon className="w-4 h-4" />
                        )}
                      </button>
                    ) : (
                      <Link
                        href={`${BASE_PATH}/${currentProduct}/${module.id}`}
                        className={`block w-full px-4 py-2 text-sm ${getNavLinkClasses(module.isModuleActive)}`}
                        onClick={onClose}
                      >
                        <span className="geist-regular">{module.name}</span>
                      </Link>
                    )}
                  </div>

                  {module.hasSubmodules && (
                    <AnimatePresence>
                      {module.isExpanded && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.2 }}
                          className="ml-[22px] mt-1 space-y-0.5 border-l border-[#EDEDED] pl-2 overflow-hidden"
                        >
                          {module.submodules?.map((submodule) => {
                            const isSubmoduleActive = isActiveLink(
                              currentProduct,
                              module.id,
                              submodule.id,
                            );
                            return (
                              <Link
                                key={submodule.id}
                                href={`${BASE_PATH}/${currentProduct}/${module.id}/${submodule.id}`}
                                className={`block px-3 py-1.5 text-sm ${getSubNavLinkClasses(isSubmoduleActive)}`}
                                onClick={() => {
                                  handleSubmoduleClick(module.id);
                                  onClose?.();
                                }}
                              >
                                <span className="geist-regular">
                                  {submodule.name}
                                </span>
                              </Link>
                            );
                          })}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-[#525252] text-sm py-8 geist-regular">
              <BookOpenIcon className="w-12 h-12 mx-auto mb-3 text-[#E5E5E5]" />
              <p>Select a product to view its documentation</p>
            </div>
          )}
        </nav>

        <div className="p-4">
          <p className="text-xs text-[#525252] geist-regular">
            Need help?{" "}
            <a href="/contact" className="text-[#171717] hover:underline">
              Contact support
            </a>
          </p>
        </div>
      </div>
    ),
    [
      currentProductData,
      currentProduct,
      modulesData,
      toggleModule,
      handleSubmoduleClick,
      isActiveLink,
      onClose,
      pathname,
    ],
  );

  return (
    <>
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="lg:hidden fixed left-0 top-0 w-72 sm:w-80 h-screen bg-white shadow-[0_0_10px_rgba(0,0,0,0.08)] overflow-y-auto z-40 border-r border-[#E5E5E5]"
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
          >
            {SidebarContent}
          </motion.div>
        )}
      </AnimatePresence>

      <aside className="docs-sidebar hidden lg:block fixed left-0 top-0 w-64 h-screen bg-white overflow-y-auto">
        {SidebarContent}
      </aside>
    </>
  );
}
